import {Button, ButtonGroup, closeCurrentDialog, DialogFooter, Tooltip} from 'platform/components';
import {Grid, Show, VStack} from 'platform/foundation';

import {isNotNil} from 'ramda';

import {
  FINANCING_ENDING_PREFLIGHT_BLOCK_REASON,
  financingEndingApi,
  type FinancingEndingPreflightResponseBody,
} from '@dms/api/sales';
import i18n from '@dms/i18n';
import {testIds} from '@dms/routes';

import type {Nullish} from 'shared/web';

import {FinancingEndingComposeForm} from '../FinancingEndingComposeForm/FinancingEndingComposeForm';
import {FinancingEndingEmailIntro} from '../FinancingEndingEmailIntro/FinancingEndingEmailIntro';
import {FinancingEndingQueryErrorReport} from '../FinancingEndingQueryErrorReport/FinancingEndingQueryErrorReport';
import {FinancingEndingRecipientSummaryCard} from '../FinancingEndingRecipientSummaryCard/FinancingEndingRecipientSummaryCard';
import type {FinancingEndingRowLabels} from '../FinancingEndingSkipSummary/FinancingEndingSkipSummary';

export type FinancingEndingEmailDialogProps = {
  rowIds: string[];
  rowLabels: FinancingEndingRowLabels[];
  dataQueryId: string | Nullish;
  onSubmitted: (bulkOperationId: string) => void;
};

const getBlockedSendReason = (preflight: FinancingEndingPreflightResponseBody) =>
  preflight.blockReason === FINANCING_ENDING_PREFLIGHT_BLOCK_REASON.OVER_LIMIT
    ? i18n.t('entity.businessCase.financingEnding.summary.overLimit', {
        cap: preflight.cap,
        plannedCount: preflight.plannedCount,
      })
    : i18n.t('entity.businessCase.financingEnding.summary.noneEligible');

export function FinancingEndingEmailDialog(props: FinancingEndingEmailDialogProps) {
  const {data, isLoading, error} = financingEndingApi.usePreflightFinancingEndingEmailQuery({
    financingEndingPreflightRequestBody: {rowIds: props.rowIds},
  });

  if (isNotNil(error)) {
    return <FinancingEndingQueryErrorReport error={error} onReported={closeCurrentDialog} />;
  }

  const summary = (
    <VStack spacing={4}>
      <FinancingEndingEmailIntro />
      <FinancingEndingRecipientSummaryCard
        preflight={data}
        isLoading={isLoading}
        rowLabels={props.rowLabels}
        dataQueryId={props.dataQueryId}
      />
    </VStack>
  );

  return (
    <VStack data-testid={testIds.businessCase.financingEnding('emailDialogContent')}>
      {isNotNil(data) && data.canSend ? (
        <FinancingEndingComposeForm
          rowIds={props.rowIds}
          preflight={data}
          summary={summary}
          onSubmitted={props.onSubmitted}
        />
      ) : (
        <VStack spacing={4}>
          <Grid columns={[1, 1, 2]}>{summary}</Grid>
          <Show when={data}>
            {(preflight) => (
              <DialogFooter>
                <ButtonGroup align="right">
                  <Button
                    variant="secondary"
                    title={i18n.t('general.actions.cancel')}
                    onClick={closeCurrentDialog}
                    data-testid={testIds.businessCase.financingEnding('cancel')}
                  />
                  <Tooltip label={getBlockedSendReason(preflight)}>
                    <Button
                      isDisabled
                      title={i18n.t('entity.businessCase.financingEnding.send.submit', {
                        count: preflight.plannedCount,
                      })}
                      data-testid={testIds.businessCase.financingEnding('send')}
                    />
                  </Tooltip>
                </ButtonGroup>
              </DialogFooter>
            )}
          </Show>
        </VStack>
      )}
    </VStack>
  );
}
